import React, { useEffect, useMemo, useRef } from 'react';
import { Box } from '@chakra-ui/react';
import {
    buildOverlapTimeline,
    getActiveSlidesAtTime,
    getBoundaryTransitionDurations,
    getSlideDurationMs,
    getSlideTransition
} from 'ts/common/components/slideshows/slideshowOverlapTimeline';
import {
    SLIDESHOW_DEFAULTS,
    getSlideBackgroundColor
} from 'ts/common/components/slideshows/slideshowUtils';
import { useTranslate } from 'ts/common/hooks';

export interface ISlideshowPlayerProps {
    /** Slideshow to display */
    slideshow: SpApi.IEventSlideshow;
    /** Global playback time in milliseconds */
    currentTime: number;
    /** Called when global time changes (optional — supply when wiring scrubbing / controls) */
    onTimeChange?: (timeMs: number) => void;
    /** Called when the highlighted slide index changes */
    onSlideIndexChange?: (index: number) => void;
    /** Transparent PNG watermark overlay URL — rendered above all slide layers */
    watermarkUrl?: string;
}

function getSlideImageUrl(slide: SpApi.ISlideshowSlide): string | null {
    const firstItem = slide.items.find((item) => item.displayUrl !== null);

    return (
        firstItem?.displayUrl?.extraLarge3 ??
        firstItem?.displayUrl?.extraLarge2 ??
        firstItem?.displayUrl?.large ??
        firstItem?.displayUrl?.medium ??
        null
    );
}

const SlideshowPlayer: React.FC<ISlideshowPlayerProps> = ({
    slideshow,
    currentTime,
    onSlideIndexChange,
    watermarkUrl
}) => {
    const t = useTranslate('studio.common.slideshowPlayer');
    const lastHighlightRef = useRef<number>(0);
    const { slides } = slideshow;

    const segmentDurations = useMemo(() => slides.map(getSlideDurationMs), [slides]);
    const effectiveOverlap = useMemo(() => getBoundaryTransitionDurations(slideshow), [slideshow]);

    const layout = useMemo(
        () =>
            buildOverlapTimeline(segmentDurations, effectiveOverlap, {
                loop: slideshow.loopToFitMusic
            }),
        [segmentDurations, effectiveOverlap, slideshow.loopToFitMusic]
    );

    const active = useMemo(() => getActiveSlidesAtTime(currentTime, layout), [currentTime, layout]);

    const highlightedIndex =
        active.secondaryIndex !== null && active.blend > 0.5
            ? active.secondaryIndex
            : active.primaryIndex;

    useEffect(() => {
        if (highlightedIndex !== lastHighlightRef.current) {
            lastHighlightRef.current = highlightedIndex;
            onSlideIndexChange?.(highlightedIndex);
        }
    }, [highlightedIndex, onSlideIndexChange]);

    const slideshowAspectRatio = slideshow.aspectRatio ?? SLIDESHOW_DEFAULTS.aspectRatio;
    const [aspectRatioW, aspectRatioH] = slideshowAspectRatio.split(':').map(Number);
    const cssAspectRatio = `${aspectRatioW} / ${aspectRatioH}`;

    const blendIn = active.secondaryIndex !== null ? active.blend : 1;
    const blendOut = active.secondaryIndex !== null ? 1 - active.blend : 1;
    const b = active.secondaryIndex !== null ? active.blend : 0;
    const activeTransitionType =
        active.secondaryIndex !== null
            ? getSlideTransition(slides[active.primaryIndex]).type
            : SLIDESHOW_DEFAULTS.slide.transition;

    const renderSlideLayer = (
        slideIndex: number,
        opacity: number,
        layer: 'incoming' | 'outgoing'
    ) => {
        if (opacity <= 0.001 || slideIndex < 0 || slideIndex >= slides.length) {
            return null;
        }

        const slide = slides[slideIndex];
        const slideImageUrl = getSlideImageUrl(slide);
        const slideBg = getSlideBackgroundColor(slide);
        const placeholderColor = slideBg === '#ffffff' ? 'black' : 'white';

        let transform = 'none';

        if (
            active.secondaryIndex !== null &&
            activeTransitionType !== 'cut' &&
            activeTransitionType !== 'fade'
        ) {
            if (activeTransitionType === 'slide') {
                const shift = layer === 'outgoing' ? -b * 100 : (1 - b) * 100;
                transform = `translateX(${shift}%)`;
            } else if (activeTransitionType === 'zoom') {
                const scale = layer === 'outgoing' ? 1 + b * 0.15 : 0.85 + b * 0.15;
                transform = `scale(${scale})`;
            }
        }

        return (
            <Box
                key={`slide-${slideIndex}`}
                position="absolute"
                top={0}
                left={0}
                width="100%"
                height="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
                opacity={opacity}
                style={{ transform }}
                pointerEvents="none"
            >
                {slideImageUrl ? (
                    <img
                        src={slideImageUrl}
                        alt={t('slideAltText', { index: slideIndex + 1 })}
                        style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain'
                        }}
                    />
                ) : (
                    <Box
                        fontSize="sm"
                        color={placeholderColor}
                        opacity={0.7}
                        textAlign="center"
                        px={4}
                    >
                        {t('photoDeleted')}
                    </Box>
                )}
            </Box>
        );
    };

    const outgoingIdx = active.primaryIndex;
    const incomingIdx = active.secondaryIndex;
    const backgroundColor = getSlideBackgroundColor(slides[highlightedIndex]);

    return (
        <Box
            flex={1}
            minH={0}
            width="100%"
            height="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            overflow="hidden"
            sx={{ containerType: 'size' }}
        >
            <Box
                position="relative"
                overflow="hidden"
                display="flex"
                alignItems="center"
                justifyContent="center"
                bg={
                    slides.length === 0 ? SLIDESHOW_DEFAULTS.slide.backgroundColor : backgroundColor
                }
                color="white"
                width={`min(100cqw, calc(100cqh * ${aspectRatioW} / ${aspectRatioH}))`}
                height={`min(100cqh, calc(100cqw * ${aspectRatioH} / ${aspectRatioW}))`}
                maxW="100%"
                maxH="100%"
                aspectRatio={cssAspectRatio}
            >
                {slides.length === 0 ? (
                    t('noSlides')
                ) : incomingIdx === null ? (
                    renderSlideLayer(outgoingIdx, 1, 'outgoing')
                ) : activeTransitionType === 'cut' ? (
                    renderSlideLayer(incomingIdx, 1, 'incoming')
                ) : (
                    <>
                        {renderSlideLayer(outgoingIdx, blendOut, 'outgoing')}
                        {renderSlideLayer(incomingIdx, blendIn, 'incoming')}
                    </>
                )}
                {watermarkUrl && (
                    <Box
                        position="absolute"
                        top={0}
                        left={0}
                        width="100%"
                        height="100%"
                        pointerEvents="none"
                    >
                        <img
                            src={watermarkUrl}
                            alt=""
                            style={{ width: '100%', height: '100%', objectFit: 'fill' }}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default SlideshowPlayer;
